import { mergeSlotProps, TextFieldProps } from "@mui/material";
import { DateValidationError, MuiPickersAdapter, PickerChangeHandlerContext } from "@mui/x-date-pickers";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { validateDate } from "@mui/x-date-pickers/validation";
import { useState } from "react";
import { Control, Controller, ControllerProps, FieldValues, Validate } from "react-hook-form";
import dayjs, { Dayjs } from "dayjs";
import { useLocalizationContext } from "@mui/x-date-pickers/internals";

const defaultErrorMessages: Record<NonNullable<DateValidationError>, string> = {
  disableFuture: "Das Datum muss in der Vergangenheit liegen",
  maxDate: "Das Datum liegt nach dem maximal zulässigen Datum",
  disablePast: "Ein Datum in der Vergangenheit ist nicht erlaubt",
  invalidDate: "Das Datum ist ungültig",
  minDate: "Das Datum liegt vor dem minimal zulässigen Datum",
  shouldDisableDate: "Dieses Datum ist nicht erlaubt",
  shouldDisableMonth: "Dieser Monat ist nicht erlaubt",
  shouldDisableYear: "Dieses Jahr ist nicht erlaubt",
};

function getTimezone(adapter: MuiPickersAdapter<Dayjs>, value: Dayjs | null): string | null {
  return value === null || !adapter.isValid(value) ? null : adapter.getTimezone(value);
}

function getDateValue(value: dayjs.ConfigType): Dayjs | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  return dayjs.isDayjs(value) ? value : dayjs(value);
}

function getValidateRules(
  rules: ControllerProps["rules"] | undefined,
  internalValidate: (value: Dayjs | null) => true | string
) {
  const validateRules: Record<string, Validate<any, FieldValues>> = {
    internal: internalValidate,
  };

  if (typeof rules?.validate === "function") {
    return {
      ...validateRules,
      custom: rules.validate,
    };
  }

  return {
    ...validateRules,
    ...rules?.validate,
  };
}

export type DatePickerElementProps = Omit<DatePickerProps, "value" | "slotProps"> & {
  name: ControllerProps["name"];
  rules?: ControllerProps["rules"];
  required?: boolean;
  control?: Control;
  inputProps?: TextFieldProps;
  helperText?: TextFieldProps["helperText"];
  textReadOnly?: boolean;
  slotProps?: DatePickerProps["slotProps"];
  overwriteErrorMessages?: Partial<typeof defaultErrorMessages>;
};

export default function DatePickerElement({
  name,
  rules,
  required,
  control,
  inputProps,
  helperText,
  textReadOnly,
  slotProps,
  overwriteErrorMessages,
  onClose,
  onChange,
  ...rest
}: DatePickerElementProps) {
  const { adapter } = useLocalizationContext();
  const [pickerError, setPickerError] = useState<DateValidationError | null>(null);

  const errorMessages = {
    ...defaultErrorMessages,
    ...overwriteErrorMessages,
  };

  const internalValidate = (value: Dayjs | null) => {
    const internalError = validateDate({
      adapter,
      value,
      timezone: rest.timezone ?? getTimezone(adapter, value) ?? "default",
      props: {
        shouldDisableDate: rest.shouldDisableDate,
        shouldDisableMonth: rest.shouldDisableMonth,
        shouldDisableYear: rest.shouldDisableYear,
        disablePast: Boolean(rest.disablePast),
        disableFuture: Boolean(rest.disableFuture),
        minDate: rest.minDate as Dayjs, // TODO: See in the future if the type is fixed and typecasting can be removed,
        maxDate: rest.maxDate as Dayjs, // TODO: See in the future if the type is fixed and typecasting can be removed
      },
    });

    return internalError === null ? true : errorMessages[internalError];
  };

  const rulesWithValidation = {
    ...rules,
    ...(required && !rules?.required && { required: "This field is required" }),
    validate: getValidateRules(rules, internalValidate),
  };

  return (
    <Controller
      name={name}
      rules={rulesWithValidation}
      control={control}
      disabled={rest.disabled}
      render={({ field, fieldState: { error } }) => (
        <DatePicker
          sx={{ width: "100%", margin: "1rem 0 0.5rem" }}
          {...rest}
          value={getDateValue(field.value)}
          inputRef={field.ref}
          onError={(newError, value) => {
            setPickerError(newError);
            rest.onError?.(newError, value);
          }}
          onClose={(...args) => {
            field.onBlur();
            onClose?.(...args);
          }}
          onChange={(newValue, changeContext: PickerChangeHandlerContext<DateValidationError>) => {
            field.onChange(newValue);
            onChange?.(newValue, changeContext);
          }}
          slotProps={{
            ...slotProps,
            textField: mergeSlotProps(slotProps?.textField, {
              required,
              helperText: error?.message || (pickerError ? errorMessages[pickerError] : null) || helperText,
              error: !!error || !!pickerError,
              onBlur: field.onBlur,
              slotProps: {
                input: {
                  readOnly: textReadOnly,
                },
              },
            }),
          }}
        />
      )}
    />
  );
}
